/**
 *
 * Home
 *
 */

import React, { memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import classNames from 'classnames';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushStateHome } from './actions';

import { selectHomeLoading, selectHomeError } from './selectors';

// antd component
import { Row, Col, Typography } from 'antd';

import { useAuthRole } from 'utils/hooks';

// components
import HomePieChart from 'components/Charts/HomePieChart';
import HomeLineChart from 'components/Charts/HomeLineChart';
import HomeLineChart2 from 'components/Charts/HomeLineChart2';
import AbsenceInfo from 'components/Charts/AbsenceInfo';
import QuickLinks from 'components/Charts/QuickLinks';
import AppVersionInformation from 'components/AppVersionInformation';
import ButtonCardIcon from 'components/ButtonCardIcon';

// global user
import { withUser } from 'engine/Contexts/User.context';

export function Home({
  // states
  history,
  error,
  loading,
  user,
  // actions
  flushState,
  //rest
  className,
}) {
  useInjectReducer({ key: 'home', reducer });
  useInjectSaga({ key: 'home', saga });

  const childClassNames = classNames('home-page-desktop', className);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    user.getAmountAbsence();
    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  // render function
  return (
    <div className={childClassNames}>
      <Helmet>
        <title>Home</title>
        <meta name="description" content="Description of Home" />
      </Helmet>
      {!!useAuthRole(['admin']) && (
        <>
          <Row className="absences">
            <Col span={24}>
              <Typography.Title level={5} className="header-title">
                Absences
              </Typography.Title>
            </Col>
            <Col span={24}>
              <Row gutter={16}>
                <Col span={9}>
                  <HomeLineChart />
                </Col>
                <Col span={9}>
                  <HomePieChart />
                </Col>
                <Col span={6}>
                  <AbsenceInfo user={user} />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Row>
                <Col span={24}>
                  <Typography.Title level={5} className="header-title">
                    Communications
                  </Typography.Title>
                </Col>
                <Col span={24}>
                  <HomeLineChart2 />
                </Col>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <Col span={24}>
                  <Typography.Title level={5} className="header-title">
                    Liens rapides
                  </Typography.Title>
                </Col>
                <Col span={24}>
                  <QuickLinks />
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}
      {!!useAuthRole(['teacher']) && (
        <div className="quickly-link teacher">
          <h3 className="quickly-link__title">Liens rapides</h3>
          <div className="quickly-link__wrapper-btn">
            <ButtonCardIcon
              title="News"
              icon="letter-close"
              className="wrapper-btn-link-button"
              action={() => {
                history.push({
                  pathname: `/communications`,
                });
              }}
            />
            <ButtonCardIcon
              title="Audiences"
              icon="group"
              className="wrapper-btn-link-button"
              action={() => {
                history.push({
                  pathname: `/audiences`,
                });
              }}
            />
          </div>
        </div>
      )}
      <AppVersionInformation
        label="Version:"
        loading={loading}
        authenticated={true}
      />
    </div>
  );
}

Home.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: selectHomeLoading(),
  error: selectHomeError(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateHome()),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
  withUser,
)(Home);
